import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { User } from "../../types/User";
import { Users } from "../../api/Users";
import { useToasts } from "../../contexts/toasts";
import { Button, Container, Modal, Row, Col } from "react-bootstrap";
import { Form } from "antd";
import classNames from "classnames";
import { TailSpin } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCancel,
  faCircle,
  faPencil,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import UserForm from "../../Components/Form/UserForm";
import dayjs from "dayjs";
import { getSubscriptionStatus } from "../../utils/subscription";

const formLayout = {
  labelCol: {
    xs: { span: 24 },
    md: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    md: { span: 16 },
  },
};

type ConfirmAction = "activate" | "deactivate" | "reissue";

const ACTION_CONFIG: Record<
  ConfirmAction,
  { title: string; body: (u?: User) => string; okText: string; variant: string }
> = {
  activate: {
    title: "Attiva abbonamento",
    body: (u) =>
      `Vuoi attivare l'abbonamento di ${u?.nome ?? ""} ${u?.cognome ?? ""}? L'utente potrà nuovamente accedere alla piattaforma.`,
    okText: "Attiva",
    variant: "success",
  },
  deactivate: {
    title: "Disattiva abbonamento",
    body: (u) =>
      `Vuoi disattivare l'abbonamento di ${u?.nome ?? ""} ${u?.cognome ?? ""}? L'utente non potrà più accedere finché non verrà riattivato.`,
    okText: "Disattiva",
    variant: "danger",
  },
  reissue: {
    title: "Reinvia password temporanea",
    body: (u) =>
      `Verrà generata una nuova password temporanea e inviata via email a ${u?.nome ?? ""} ${u?.cognome ?? ""}. La password attuale non sarà più valida. Procedere?`,
    okText: "Reinvia",
    variant: "warning",
  },
};

export default function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [user, setUser] = useState<User>();
  const [userForm] = Form.useForm();
  const [editLoading, setEditLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isNewUser = !id || id === "nuovo";

  const initUser = useCallback(() => {
    Users.getUser(+id!).then(
      ({ data }) => {
        setUser(data.user);
        userForm.setFieldsValue({
          ...data.user,
          birthDate: data.user.birthDate ? dayjs(data.user.birthDate) : null,
          expiring: data.user.expiring ? dayjs(data.user.expiring) : null,
        });
      },
      () => {
        navigate(-1);
        showToast(
          "user-error",
          "Errore nel caricamento dell'utente",
          "",
          "danger"
        );
      }
    );
  }, [navigate, showToast, id, userForm]);

  const handleFieldChange = useCallback(
    (key: string) => (value: string) => {
      // Peso/altezza are optional. An empty (or non-numeric) input clears the
      // field to `undefined` instead of coercing to 0 / NaN, so a blank value
      // is simply omitted from the payload and never trips validation.
      const trimmed = (value ?? "").trim();
      const parsed = trimmed === "" ? undefined : Number(trimmed);
      const next = parsed === undefined || Number.isNaN(parsed) ? undefined : parsed;
      setUser((previous) => ({
        ...previous!,
        [key]: next,
      }));
      userForm.setFieldValue(key, next);
    },
    [userForm]
  );

  const retrieveId = useCallback(() => {
    if (!isNewUser && !isNaN(+id!)) initUser();
    else {
      setUser({
        ...new User(),
        birthDate: "",
        expiring: undefined,
      });
      userForm.setFieldsValue(new User());
    }
  }, [id, isNewUser, initUser, userForm]);

  const reset = useCallback(() => {
    setUser(undefined);
    retrieveId();
  }, [retrieveId]);

  useEffect(() => {
    reset();
  }, [reset]);

  const handleSubmit = useCallback(
    (values: any) => {
      setEditLoading(true);
      const base = {
        ...values,
        birthDate: values.birthDate ? values.birthDate.toISOString() : undefined,
      };

      if (!isNewUser) {
        // Subscription deadline + access state are managed via the dedicated
        // actions, so they are intentionally excluded from the profile save.
        const profile = { ...base };
        delete profile.expiring;
        delete profile.isActive;
        Users.updateUser(+id!, profile).then(
          () => {
            setEditLoading(false);
            showToast("user-success", "Utente aggiornato con successo", "", "success");
            reset();
          },
          () => {
            setEditLoading(false);
            showToast("user-error", "Errore nell'aggiornamento dell'utente", "", "danger");
          }
        );
      } else {
        const payload = {
          ...base,
          expiring: values.expiring ? values.expiring.toISOString() : undefined,
        };
        Users.createUser(payload).then(
          () => {
            setEditLoading(false);
            showToast("user-success", "Utente creato con successo", "", "success");
            navigate("/utenti");
          },
          () => {
            setEditLoading(false);
            showToast("user-error", "Errore nella creazione dell'utente", "", "danger");
          }
        );
      }
    },
    [isNewUser, id, showToast, navigate, reset]
  );

  // Immediate subscription-deadline save (renews + reactivates on the BE when
  // the date is in the future), independent from the profile form.
  const saveExpiry = useCallback(() => {
    if (!user?.id) return;
    const expiringVal = userForm.getFieldValue("expiring");
    setEditLoading(true);
    Users.updateUser(user.id, {
      expiring: expiringVal ? dayjs(expiringVal).toISOString() : null,
    }).then(
      () => {
        setEditLoading(false);
        showToast("user-success", "Scadenza abbonamento aggiornata", "", "success");
        reset();
      },
      () => {
        setEditLoading(false);
        showToast("user-error", "Errore nell'aggiornamento della scadenza", "", "danger");
      }
    );
  }, [user, userForm, showToast, reset]);

  const runConfirmedAction = useCallback(() => {
    if (!confirmAction || !user?.id) return;
    setActionLoading(true);
    const done = (msg: string) => {
      setActionLoading(false);
      setConfirmAction(null);
      showToast("user-success", msg, "", "success");
      reset();
    };
    const fail = (msg: string) => {
      setActionLoading(false);
      showToast("user-error", msg, "", "danger");
    };

    if (confirmAction === "reissue") {
      Users.reIssueTemporaryPassword(user.id).then(
        () => done("Password temporanea inviata correttamente"),
        () => fail("Errore nell'invio della password temporanea")
      );
    } else {
      const isActive = confirmAction === "activate";
      Users.updateUser(user.id, { isActive }).then(
        () =>
          done(
            isActive
              ? "Abbonamento attivato correttamente"
              : "Abbonamento disattivato correttamente"
          ),
        () => fail("Errore nell'aggiornamento dell'utente")
      );
    }
  }, [confirmAction, user, showToast, reset]);

  const statusHex = useMemo(
    () => (user && !isNewUser ? getSubscriptionStatus(user).hex : undefined),
    [user, isNewUser]
  );

  const renderToolbar = useCallback(() => {
    return (
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">
            {isNewUser ? "Nuovo" : "Modifica"} Utente{" "}
            {!isNewUser && (
              <span>
                - {user?.nome} {user?.cognome}
                <FontAwesomeIcon
                  icon={faCircle}
                  className="ms-2"
                  style={{ color: statusHex }}
                />
              </span>
            )}
          </h4>
        </Col>

        <Col xs={6} md={4} className="text-end">
          <button
            disabled={editLoading}
            type="submit"
            className={classNames("button-primary", {
              "button-disabled": editLoading,
            })}
          >
            {editLoading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={isNewUser ? faSave : faPencil} />
                <span className="d-none d-sm-inline ms-2">
                  {isNewUser ? "Salva Utente" : "Modifica Dati"}
                </span>
              </>
            )}
          </button>
          <button
            type="button"
            disabled={editLoading}
            onClick={() => navigate("/utenti")}
            className={classNames("button-secondary", "ms-2", {
              "button-disabled": editLoading,
            })}
          >
            <FontAwesomeIcon icon={faCancel} />
            <span className="d-none d-sm-inline ms-2">Annulla</span>
          </button>
        </Col>
      </Row>
    );
  }, [editLoading, user, isNewUser, statusHex, navigate]);

  const renderUser = useCallback(() => {
    return (
      <Row>
        <UserForm
          user={user}
          isNewUser={isNewUser}
          disabled={editLoading}
          onPesoChange={handleFieldChange("peso")}
          onAltezzaChange={handleFieldChange("altezza")}
          onActivate={() => setConfirmAction("activate")}
          onDeactivate={() => setConfirmAction("deactivate")}
          onReissue={() => setConfirmAction("reissue")}
          onSaveExpiry={saveExpiry}
        />
      </Row>
    );
  }, [user, isNewUser, editLoading, handleFieldChange, saveExpiry]);

  const renderAsyncContent = useCallback(() => {
    return user ? (
      renderUser()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }, [user, renderUser]);

  const cfg = confirmAction ? ACTION_CONFIG[confirmAction] : null;

  return (
    <Container fluid="md" className="mt-5">
      <Form {...formLayout} form={userForm} onFinish={handleSubmit}>
        {renderToolbar()}
        <hr />
        {renderAsyncContent()}
      </Form>

      <Modal show={!!confirmAction} onHide={() => setConfirmAction(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{cfg?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{cfg?.body(user)}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={() => setConfirmAction(null)}
            disabled={actionLoading}
          >
            Annulla
          </Button>
          <Button
            variant={cfg?.variant}
            size="sm"
            onClick={runConfirmedAction}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>{cfg?.okText}</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
